import type { Properties } from 'hast';
import type { Root } from 'mdast';
import { TextDirective } from 'mdast-util-directive';
import type { Plugin } from 'unified';
import { visit } from 'unist-util-visit';

interface DirectiveDataWithHast {
  hName?: string;
  hProperties?: Properties;
  [key: string]: unknown;
}

/**
 * Remark plugin that transforms emoji directives into Emoji components
 *
 * Syntax: :emoji[👋]
 *
 * The directive accepts an emoji character in the content and transforms it
 * into an Emoji component that renders a non-colored OpenMoji SVG
 *
 * @example
 * ```markdown
 * Hello :emoji[👋] world!
 * ```
 */
export const remarkEmoji: Plugin<[], Root> = () => (tree) => {
  const visitDirective = (node: TextDirective) => {
    if (node.name !== 'emoji') {
      return;
    }

    // Extract emoji from children
    let emoji = '';
    if (node.children && node.children.length > 0) {
      // The emoji is typically in a text node child
      const textNode = node.children.find((child) => child.type === 'text') as
        | { value?: string }
        | undefined;
      if (textNode && textNode.value) {
        emoji = textNode.value;
      }
    }

    // If no emoji found in children, check attributes
    if (!emoji && node.attributes?.emoji) {
      emoji = String(node.attributes.emoji);
    }

    // Transform the directive node into an emoji component node
    // react-markdown will map this to the Emoji React component via the components prop
    const data = (node.data || {}) as DirectiveDataWithHast;
    data.hName = 'emoji';
    data.hProperties = {
      'aria-label': node.attributes?.['aria-label']
        ? String(node.attributes['aria-label'])
        : undefined,
    };
    node.data = data;

    // Keep the emoji as a text child node so React can render it as children
    if (emoji) {
      node.children = [
        {
          type: 'text',
          value: emoji,
        } as (typeof node.children)[number],
      ];
    }
  };

  // Only process textDirective since user specified textDirective only
  visit(tree, 'textDirective', visitDirective);
};
